/* eslint-disable no-console */
import { Segment } from '@meowwolf/cookie-consent-utils'
import {
  IS_DEVELOPMENT_ENV,
  IS_PLAYGROUND_DEPLOYMENT,
  IS_PREVIEW_ENV,
  IS_PROD_PLAYGROUND_DEPLOYMENT,
  IS_STAGING_DEPLOYMENT,
} from '@/config/environments.config'

type TrackingLoggerTypeT = 'segment' | 'gtm'

type QueuedLoggerT = {
  log: (msg: string, other?: object | unknown[]) => void
  print: () => void
}

type LoggerT = {
  log: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

export const isLoggingEnabled = (): boolean => [
  IS_DEVELOPMENT_ENV,
  IS_PREVIEW_ENV,
  IS_STAGING_DEPLOYMENT,
  IS_PLAYGROUND_DEPLOYMENT,
  IS_PROD_PLAYGROUND_DEPLOYMENT,
].some(Boolean)

/**
 * console wrapper to only log in non-production environments
 * @returns {LoggerT}
 */
export const logger = (enabled = isLoggingEnabled()): LoggerT => ({
  log: (...args: unknown[]): void => {
    if (!enabled) return
    console.log(...args)
  },
  warn: (...args: unknown[]): void => {
    if (!enabled) return
    console.warn(...args)
  },
  error: (...args: unknown[]): void => {
    if (!enabled) return
    console.error(...args)
  },
})

/**
 * Queued logger for when we need all log messages to output at the same time
 * This is most useful for Vercel logs
 *
 * @param enabled - this value will override the default enabled value.
 * Default enabled value is based on the environment and is set to not log in production
 */
export const queuedLogger = (enabled: boolean): QueuedLoggerT => {
  let queue: string[] = []
  return {
    log: (msg: string, other?: object | unknown[]): void => {
      if (!enabled) return
      queue.push(msg)
      if (other) {
        queue.push(JSON.stringify(other, null, 2))
      }
    },
    print: (): void => {
      if (!enabled) return
      // enabled here overrides the default enabled value
      // allowing for logging in production if desired
      logger(enabled).log(queue.join(' || '))
      // reset queue
      queue = []
    },
  }
}

/**
 * Threshold (in bytes) above which a payload is considered large enough to warn about.
 * Mirrors the 1 MB server-action body limit that has caused 413s.
 */
export const PAYLOAD_SIZE_WARNING_THRESHOLD_BYTES = 1024 * 1024

/**
 * Measures the serialized byte size of a payload and logs it.
 *
 * Payloads at or above {@link PAYLOAD_SIZE_WARNING_THRESHOLD_BYTES} are warned about in
 * every environment (including production) so we can diagnose oversized responses/413s.
 * Smaller payloads are logged only in non-production environments via {@link logger}.
 *
 * @param label - Human-readable identifier for the call site (e.g. "calendar").
 * @param payload - The value whose serialized size should be measured.
 * @returns The measured size in bytes (0 if the payload could not be serialized).
 */
export const logPayloadSize = (label: string, payload: unknown): number => {
  let bytes = 0
  try {
    bytes = Buffer.byteLength(JSON.stringify(payload) ?? '', 'utf8')
  } catch {
    // Payload could not be serialized (e.g. circular reference); nothing to measure.
    return 0
  }

  const megabytes = (bytes / (1024 * 1024)).toFixed(2)
  const message = `[payload size] ${label}: ${bytes} bytes (${megabytes} MB)`

  if (bytes >= PAYLOAD_SIZE_WARNING_THRESHOLD_BYTES) {
    console.warn(`${message} — exceeds ${PAYLOAD_SIZE_WARNING_THRESHOLD_BYTES} byte threshold`)
  } else {
    logger().log(message)
  }

  return bytes
}

/**
 * Tracking logger
 * Logger specifically for tracking
 * @param type
 * @param name
 * @param rest
 */
export const trackingLogger = (
  type: TrackingLoggerTypeT,
  name: string | Segment.SegmentEvent,
  ...rest: unknown[]
): void => {
  logger().log(
    `${type}: ${name}`,
    rest,
  )
}
